<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { onMount } from 'svelte';
    import { __, getActualKey, generateElementId } from '@oat-sa-private/ui-core';
    import { IconBarButton } from '@oat-sa-private/ui-elements';
    import { FloatingBar } from '@oat-sa-private/ui-components';
    import { testSessionStatus } from '../../../session/sessionStates.js';
    import { getTestSessionStatusStore } from '../../../testsStateStore.js';
    import { actionKeys } from './readAloudActionKeys.js';
    import ReadAloudSettingsDrawer from './ReadAloudSettingsDrawer.svelte';

    /**
     * Component is used to show readAloud toolbar.
     * Should be mounted in floating toolbar container.
     * Button states should be managed by consumer.
     */

    /**
     * @typedef {Object} Props
     * @property {String} serviceCallId
     * @property {Boolean} [clickToSpeakEnable]
     * @property {Boolean} [playOnClickToggled]
     * @property {Boolean} [playOnClickPlaying]
     * @property {Boolean} [playSelectionToggled]
     * @property {Boolean} [playSelectionPlaying]
     * @property {Boolean} [playAllPlaying]
     * @property {Object} [readAloudSettings]
     * @property {Boolean} [disabled]
     * @property {Boolean} [autofocus]
     * @property {Function} onAction
     * @property {Function} onChange
     * @property {Function} onClose
     */

    /** @type {Props} */
    let {
        serviceCallId,
        clickToSpeakEnable = false,
        playOnClickToggled = false,
        playOnClickPlaying = false,
        playSelectionToggled = false,
        playSelectionPlaying = false,
        playAllPlaying = false,
        readAloudSettings = {},
        disabled = false,
        autofocus = false,
        onAction,
        onChange,
        onClose
    } = $props();

    // svelte-ignore state_referenced_locally
    const statusStore = getTestSessionStatusStore(serviceCallId);
    const ariaDescribedByIdSelection = generateElementId('ra-sel');
    const ariaDescribedByIdPlay = generateElementId('ra-play');

    let actionsElement = $state();

    /**
     * Keydown handler
     * @param {KeyboardEvent} e event
     */
    function handleKeyDown(e) {
        const pressedKey = getActualKey(e);
        switch (pressedKey) {
            case 'esc': {
                onClose?.();
                break;
            }
        }
    }

    /**
     * Action button click handler
     * @param {String} key
     */
    function handleActionClick(key) {
        onAction?.({ key });
    }

    /**
     * Click handler to avoid triggering TTS from UI clicks
     * @param {MouseEvent} e
     */
    function handleRootClick(e) {
        e.stopPropagation();
    }

    /**
     * Get list of buttons
     * @returns {Array<HTMLElement>}
     */
    function getFocusableElements() {
        return Array.from(actionsElement.querySelectorAll('.icon-bar-btn'));
    }

    onMount(() => {
        if (autofocus && actionsElement) {
            getFocusableElements()[0].focus();
        }
    });
</script>

<style>
    .readAloud-bar {
        & .footer {
            & .instruction {
                min-height: 3.25rem;
                max-width: 30rem;
                padding: 0 1.5rem;
                margin: 0;
                font-size: var(--fontsize-body-xs);
            }
        }
    }
</style>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    class="readAloud-bar"
    class:has-footer={playSelectionToggled || playOnClickToggled || readAloudSettings.open}
    class:hidden={$statusStore === testSessionStatus.overlay}
    onkeydown={handleKeyDown}
    onclick={handleRootClick}>
    <FloatingBar title={__('read aloud')}>
        <!-- first slot -->
        <div class="actions do-not-read" bind:this={actionsElement}>
            <IconBarButton
                label={__('Continuous reading')}
                icon={playAllPlaying ? 'stop-16' : 'play-16'}
                shape="circular"
                toggled={playAllPlaying}
                ariaPressed={playAllPlaying}
                ariaDescribedBy={ariaDescribedByIdPlay}
                dataTestId="readaloud-play"
                {disabled}
                on:click={() => handleActionClick(actionKeys.playAll)} />
            {#if clickToSpeakEnable}
                <IconBarButton
                    label={__('Click to read')}
                    icon={playOnClickPlaying ? 'stop-16' : 'play-on-click-16'}
                    shape="circular"
                    toggled={playOnClickToggled}
                    ariaPressed={playOnClickToggled}
                    ariaDescribedBy={ariaDescribedByIdSelection}
                    dataTestId="readaloud-play-on-click"
                    {disabled}
                    on:click={() => handleActionClick(actionKeys.playOnClick)} />
            {:else}
                <IconBarButton
                    label={__('Select and read')}
                    icon={playSelectionPlaying ? 'stop-16' : 'play-from-16'}
                    shape="circular"
                    toggled={playSelectionToggled}
                    ariaPressed={playSelectionToggled}
                    ariaDescribedBy={ariaDescribedByIdSelection}
                    dataTestId="readaloud-play-selection"
                    {disabled}
                    on:click={() => handleActionClick(actionKeys.playSelection)} />
            {/if}
            <IconBarButton
                label={__('ReadAloud settings')}
                icon="cogwheel-16"
                shape="circular"
                toggled={readAloudSettings.open}
                ariaPressed={readAloudSettings.open}
                dataTestId="readaloud-settings"
                {disabled}
                on:click={() => handleActionClick(actionKeys.settings)} />
        </div>
        <!-- second slot -->
        {#snippet footer()}
            <div class="footer">
                {#if !disabled && playSelectionToggled}
                    <p class="instruction">{__('Select text to read aloud')}</p>
                {/if}
                {#if !disabled && clickToSpeakEnable && playOnClickToggled}
                    <p class="instruction">{__('Click on any text to read aloud')}</p>
                {/if}
                {#if readAloudSettings.open}
                    <ReadAloudSettingsDrawer {readAloudSettings} {disabled} {onChange} />
                {/if}
            </div>
        {/snippet}
    </FloatingBar>
    <!-- hidden ARIA elements: -->
    <div id={ariaDescribedByIdSelection} class="hidden">
        {__(
            'Press enter or space to activate select and read. To move the next available action, use the arrow keys. To close press escape.'
        )}
    </div>
    <div id={ariaDescribedByIdPlay} class="hidden">
        {__(
            'Press enter or space to activate continuous reading. If activated press enter or space to stop the reading.'
        )}
    </div>
</div>
